import { Box, Stack } from "@mui/material";
import { FC } from "react";
import { LayoutProps } from "../types";
import ImageBlock from "../blocks/image-block";
import HeaderBlock from "../blocks/header-block";
import ContentBlock from "../blocks/content-block";
import StatsBlock from "../blocks/stats-block";
import ActionsBlock from "../blocks/actions-block";
import MetaBlock from "../blocks/meta-block";

const VerticalLayout: FC<LayoutProps> = ({
  blocks,
  imagePosition,
  spacing,
  theme,
  containerProps,
  showBorder = true,
  showBoxShadow = true,
  disableHover = false,
}) => {
  const getSpacingValue = () => {
    switch (spacing) {
      case "compact":
        return 2;
      case "spacious":
        return 4;
      default:
        return 3;
    }
  };

  const getPaddingValue = () => {
    switch (spacing) {
      case "compact":
        return 3;
      case "spacious":
        return 5;
      default:
        return 4;
    }
  };

  const getContainerStyles = () => {
    const baseStyles = {
      position: "relative" as const,
      width: "100%",
      height: "100%",
    };

    const borderStyles = showBorder
      ? {
          border: "1px solid #e0e0e0",
          borderRadius: "12px",
        }
      : {};

    const shadowStyles = showBoxShadow
      ? {
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }
      : {};

    const hoverStyles = !disableHover
      ? {
          transition: "all 0.3s ease",
          "&:hover": {
            ...(showBoxShadow && {
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            }),
            transform: "translateY(-2px)",
          },
        }
      : {};

    return {
      ...baseStyles,
      ...borderStyles,
      ...shadowStyles,
      ...hoverStyles,
      ...containerProps?.sx,
    };
  };

  const isImageTop = imagePosition === "top" || imagePosition === "left";

  return (
    <Box sx={getContainerStyles()} {...containerProps}>
      {/* Badge removed - use images with embedded badges instead */}

      <Stack spacing={0} sx={{ height: "100%" }}>
        {/* Image Section */}
        {blocks.image && isImageTop && (
          <Box sx={{ position: "relative" }}>
            <ImageBlock image={blocks.image} theme={theme} />
          </Box>
        )}

        {/* Content Section */}
        <Box sx={{ padding: getPaddingValue(), flex: 1 }}>
          <Stack spacing={getSpacingValue()} sx={{ height: "100%" }}>
            {/* Header */}
            {blocks.header && <HeaderBlock header={blocks.header} theme={theme} />}

            {/* Content */}
            {blocks.content && <ContentBlock content={blocks.content} theme={theme} />}

            {/* Stats */}
            {blocks.stats && <StatsBlock stats={blocks.stats} theme={theme} />}

            {/* Spacer to push bottom content to bottom */}
            <Box sx={{ flex: 1 }} />

            {/* Bottom row with Meta and Actions */}
            {(blocks.meta || blocks.actions) && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                {/* Meta (Date) on the left */}
                {blocks.meta && (
                  <Box>
                    <MetaBlock meta={blocks.meta} theme={theme} />
                  </Box>
                )}

                {/* Actions (Link) on the right */}
                {blocks.actions && (
                  <Box>
                    <ActionsBlock actions={blocks.actions} theme={theme} />
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </Box>

        {/* Image Section - Bottom */}
        {blocks.image && !isImageTop && (
          <Box sx={{ position: "relative" }}>
            <ImageBlock image={blocks.image} theme={theme} />
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default VerticalLayout;
